/**
 * Datos de la re-vinculación asistida (T039; wiring §4.3 y §8.1). TanStack Query sobre
 * `api/client.ts`.
 *
 * Las tres rutas son **públicas** del realm staff (`config: { public: true }` en la API): no hay
 * cookie ni sesión todavía. El cliente firma igualmente con la clave de este teléfono cuando ya
 * existe, y aquí se pasa `includeDeviceId: false` en las tres: el dispositivo aún no está enrolado,
 * así que no existe un `dev_…` que declarar (en `claim` el servidor deriva el id de la clave de la
 * solicitud y verifica la firma contra ella).
 *
 * `POST /rebind-requests` responde **202** exista o no un trabajador `ACTIVE` con ese documento: la
 * UI muestra la misma pantalla de seguimiento en ambos casos (spec US3, escenario 6). La excepción
 * es el cupo de tres solicitudes por día operativo: la cuarta responde `429 RATE_LIMITED` con
 * `details.reason = 'DAILY_LIMIT'` y su propio mensaje, que el formulario muestra sin navegar
 * (spec US3, escenario 5).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useRef } from 'react';

import {
  ClaimResponseSchema,
  RebindRequestBodySchema,
  RebindRequestCreatedSchema,
  RebindStatusResponseSchema,
  type ClaimResponse,
  type DocumentType,
  type RebindRequestBody,
  type RebindRequestCreated,
  type RebindStatusResponse,
} from '@fermin/contracts';

import { api, ApiError } from '../../api/client';
import { exportPublicJwk, getOrCreateKeyPair } from '../../device/keys';
import { detectPlatform, displayMode, userAgent } from '../../device/platform';

import { STAFF_ME_QUERY_KEY } from './api';
import { DEVICE_KEY_QUERY_KEY } from './device-key-query';
import { clearRebindRequest } from './rebind-store';

export const REBIND_QUERY_KEY = ['rebind'] as const;

/**
 * Cadencia del polling de `GET /rebind-requests/{ref}` mientras la solicitud siga `PENDING`.
 * 6 s = 600 lecturas/hora por solicitud, con margen bajo el límite de 720/h del endpoint.
 */
export const REBIND_POLL_INTERVAL_MS = 6_000;

/** Techo del backoff exponencial cuando la API responde `429 RATE_LIMITED`. */
export const REBIND_POLL_MAX_INTERVAL_MS = 30_000;

/** Validación del número de documento en el formulario (misma regla que el contrato). */
export const RebindDocumentNumberSchema = RebindRequestBodySchema.shape.document_number;

function rebindPath(ref: string, suffix = ''): string {
  return `/rebind-requests/${encodeURIComponent(ref)}${suffix}`;
}

// ---------------------------------------------------------------------------
// POST /rebind-requests
// ---------------------------------------------------------------------------

/** Lo que escribe el trabajador; el resto del cuerpo lo aporta el dispositivo. */
export interface RebindDocumentInput {
  document_type: DocumentType;
  document_number: string;
}

/**
 * Cuerpo de `RebindRequestBodySchema`. Crea la clave ECDSA P-256 no exportable de este teléfono si
 * aún no existe: es la clave que el admin aprobará y con la que se firmará el `claim`.
 */
export async function buildRebindBody(input: RebindDocumentInput): Promise<RebindRequestBody> {
  await getOrCreateKeyPair();
  return RebindRequestBodySchema.parse({
    document_type: input.document_type,
    document_number: input.document_number,
    public_key_jwk: await exportPublicJwk(),
    platform: detectPlatform(),
    display_mode: displayMode(),
    user_agent: userAgent(),
  });
}

export function requestRebind(body: RebindRequestBody): Promise<RebindRequestCreated> {
  return api.post('/rebind-requests', {
    body,
    schema: RebindRequestCreatedSchema,
    includeDeviceId: false,
  });
}

export function useRequestRebind(): UseMutationResult<
  RebindRequestCreated,
  ApiError,
  RebindDocumentInput
> {
  return useMutation<RebindRequestCreated, ApiError, RebindDocumentInput>({
    mutationFn: async (input) => requestRebind(await buildRebindBody(input)),
  });
}

// ---------------------------------------------------------------------------
// GET /rebind-requests/{ref}
// ---------------------------------------------------------------------------

export function fetchRebindStatus(ref: string): Promise<RebindStatusResponse> {
  return api.get(rebindPath(ref), {
    schema: RebindStatusResponseSchema,
    includeDeviceId: false,
  });
}

/**
 * Polling cada 6 s mientras siga `PENDING`; en cuanto se resuelve, se detiene.
 *
 * Si la API responde `429 RATE_LIMITED` la cadencia se duplica (backoff simple) hasta
 * `REBIND_POLL_MAX_INTERVAL_MS`, y vuelve a 6 s en cuanto una lectura sale bien: seguir sondeando
 * al mismo ritmo solo alargaría el bloqueo.
 */
export function useRebindStatus(
  ref: string,
  options: { enabled?: boolean } = {},
): UseQueryResult<RebindStatusResponse, ApiError> {
  const intervalRef = useRef(REBIND_POLL_INTERVAL_MS);
  return useQuery<RebindStatusResponse, ApiError>({
    queryKey: [...REBIND_QUERY_KEY, 'status', ref],
    queryFn: async () => {
      try {
        const status = await fetchRebindStatus(ref);
        intervalRef.current = REBIND_POLL_INTERVAL_MS;
        return status;
      } catch (error) {
        if (error instanceof ApiError && error.status === 429) {
          intervalRef.current = Math.min(intervalRef.current * 2, REBIND_POLL_MAX_INTERVAL_MS);
        }
        throw error;
      }
    },
    enabled: options.enabled ?? true,
    retry: false,
    refetchInterval: (query) =>
      query.state.data === undefined || query.state.data.status === 'PENDING'
        ? intervalRef.current
        : false,
    refetchIntervalInBackground: false,
  });
}

// ---------------------------------------------------------------------------
// POST /rebind-requests/{ref}/claim
// ---------------------------------------------------------------------------

export function claimRebind(ref: string): Promise<ClaimResponse> {
  return api.post(rebindPath(ref, '/claim'), {
    schema: ClaimResponseSchema,
    includeDeviceId: false,
  });
}

/**
 * Reclama la vinculación aprobada: la respuesta trae la cookie de sesión, así que a partir de aquí
 * el trabajador queda autenticado en este teléfono. Se olvida la solicitud y se refresca `GET /me`.
 *
 * `DEVICE_KEY_QUERY_KEY` se deja en `true` a mano: `HomeGate` la cachea con
 * `staleTime: Number.POSITIVE_INFINITY` y, si este teléfono ya había pasado por `/` sin clave,
 * seguiría leyendo `false` y rebotaría a `/dispositivo-no-registrado` justo después del `claim`.
 */
export function useClaimRebind(ref: string): UseMutationResult<ClaimResponse, ApiError, void> {
  const queryClient = useQueryClient();
  return useMutation<ClaimResponse, ApiError, void>({
    mutationFn: () => claimRebind(ref),
    onSuccess: async () => {
      clearRebindRequest();
      queryClient.setQueryData(DEVICE_KEY_QUERY_KEY, true);
      await queryClient.invalidateQueries({ queryKey: STAFF_ME_QUERY_KEY });
    },
  });
}
